import {CommonModule} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  BankAccountType,
  PixKeyType,
  RecipientBankAccount,
} from '@gatewee/types/recipient-bank-account';
import {
  CreateRecipientBankAccountPayload,
  PixKeyCheckResponse,
} from '@gatewee/types/pix-key-check';
import {
  cpfCnpjFormattedMaxLength,
  cpfCnpjNoLiveMask,
  formatCpfCnpjDisplay,
  pixKeyDocumentMask,
} from '@gatewee/utils/helpers/cpf-cnpj-input.helper';
import {
  formatRecipientAccount,
  formatRecipientAgency,
} from '@gatewee/utils/helpers/format-bank-account-display';
import {resolveCompeDisplayName} from '@gatewee/utils/helpers/compe-display-name';
import {
  formatPixKeyForDisplay,
  inferPixKeyKind,
  mapInferKindToBankAccountPixKeyType,
  maskForPixKeyKind,
  PixKeyInferKind,
  sanitizePixKeyForApi,
  validatePixKeyByKind,
} from '@gatewee/utils/helpers/pix-key-infer.helper';
import {mapPixKeyCheckToBankAccountPayload} from '@gatewee/utils/helpers/pix-key-check.helper';
import {recipientBankAccountPixKeyCrossValidator} from '@gatewee/utils/helpers/pix-key.helper';
import {validateCPFOrCNPJ} from '@gatewee/utils/validators/cpf-cnpjs-validators';
import {UtilsModule} from '@gatewee/utils';
import {POST_SAVE_REDIRECT_DELAY_MS} from '@gatewee/utils/helpers/post-save-redirect-delay';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService} from '@taiga-ui/core';
import {Observable} from 'rxjs';
import {GateweeBankSvgLogoComponent} from '../gatewee-bank-svg-logo/gatewee-bank-svg-logo.component';
import {GateweeButtonComponent} from '../gatewee-button/gatewee-button.component';
import {GateweeIconComponent} from '../gatewee-icon/gatewee-icon.component';
import {GateweeInputV2Component} from '../gatewee-input-v2/gatewee-input-v2.component';
import {GateweeSelectV3Component} from '../gatewee-select-v3/gatewee-select-v3.component';
import {GateweeStepperComponent} from '../gatewee-stepper/gatewee-stepper.component';

export interface BankAccountRegistrationActions {
  /** Resolves the DICT lookup. The domain app must send `recipient.id`; recipient app must not. */
  checkKeyFn: (
    pixKey: string,
    pixKeyType: PixKeyType,
  ) => Observable<PixKeyCheckResponse>;
  createFn: (
    payload: CreateRecipientBankAccountPayload,
  ) => Observable<RecipientBankAccount>;
}

@Component({
  selector: 'gatewee-bank-account-registration',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    UtilsModule,
    GateweeBankSvgLogoComponent,
    GateweeButtonComponent,
    GateweeIconComponent,
    GateweeInputV2Component,
    GateweeSelectV3Component,
    GateweeStepperComponent,
  ],
  templateUrl: './gatewee-bank-account-registration.component.html',
})
export class GateweeBankAccountRegistrationComponent implements OnInit {
  @Input({required: true}) actions!: BankAccountRegistrationActions;
  /** Set to `false` when the provider's `pixKeyCheck` capability is known to be disabled. */
  @Input() pixKeyCheckEnabled = true;

  @Output() created = new EventEmitter<RecipientBankAccount>();
  @Output() cancelled = new EventEmitter<void>();

  useStepperFlow = true;
  step = 0;
  maxAllowedIndex = 0;
  reviewSource: 'lookup' | 'manual' | null = null;
  wizardSteps: {label: string}[] = [];

  lookupForm!: FormGroup;
  manualForm!: FormGroup;

  lookupSubmitting = false;
  reviewSubmitting = false;

  lookupErrorMessage: string | null = null;
  lookupNotFound = false;
  /** Set once a check-key call responds with 501 — drops PIX step for the rest of the session. */
  capabilityUnavailable = false;

  checkKeyResponse: PixKeyCheckResponse | null = null;
  reviewPayload: CreateRecipientBankAccountPayload | null = null;

  /**
   * When the key is `cpf_or_phone`, user picks document vs phone (same pattern as
   * PIX-area withdrawal). Cleared when the inferred kind is no longer ambiguous.
   */
  manualLookupKind: 'cpf' | 'phone' | null = null;

  pixKeyTypeOptions: {value: PixKeyType; label: string}[] = [];
  accountTypeOptions: {value: BankAccountType; label: string}[] = [];

  readonly holderDocumentMask = cpfCnpjNoLiveMask;
  readonly holderDocumentMaxLength = cpfCnpjFormattedMaxLength;

  manualPixKeyMask = '';
  manualPixKeyMaxLength = 320;

  readonly canNavigateStepper = (targetIndex: number): boolean => {
    return !(
      this.showPixLookupStep &&
      targetIndex === this.accountDataStepIndex &&
      this.reviewSource === 'lookup'
    );
  };

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly fb: FormBuilder,
    private readonly alerts: TuiAlertService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.useStepperFlow = this.pixKeyCheckEnabled;
    this.buildLookupForm();
    this.buildManualForm();
    this.refreshLocalizedUi();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshLocalizedUi());
  }

  get showPixLookupStep(): boolean {
    return this.useStepperFlow && !this.capabilityUnavailable;
  }

  get accountDataStepIndex(): number {
    return this.showPixLookupStep ? 1 : 0;
  }

  get reviewStepIndex(): number {
    return this.showPixLookupStep ? 2 : 1;
  }

  get showAccountDataBackToPix(): boolean {
    return this.showPixLookupStep;
  }

  get lookupPixKeyKind(): PixKeyInferKind {
    return inferPixKeyKind(String(this.lookupForm?.get('pixKey')?.value ?? ''));
  }

  get effectiveLookupKind(): PixKeyInferKind {
    if (this.manualLookupKind === 'cpf') {
      return 'cpf';
    }
    if (this.manualLookupKind === 'phone') {
      return 'phone';
    }
    return this.lookupPixKeyKind;
  }

  get lookupPixKeyMask(): string {
    return maskForPixKeyKind(this.effectiveLookupKind);
  }

  get showAmbiguousKindRow(): boolean {
    return this.lookupPixKeyKind === 'cpf_or_phone';
  }

  get needsAmbiguousKindChoice(): boolean {
    return this.lookupPixKeyKind === 'cpf_or_phone' && !this.manualLookupKind;
  }

  get canSubmitLookup(): boolean {
    return (
      !!this.lookupForm &&
      this.lookupForm.valid &&
      !this.lookupSubmitting &&
      !this.needsAmbiguousKindChoice
    );
  }

  get reviewBankName(): string {
    if (!this.reviewPayload) {
      return '';
    }
    return resolveCompeDisplayName(
      this.reviewPayload.compeCode,
      this.reviewPayload.ispbCode,
    ).trim();
  }

  get reviewAgencyDisplay(): string {
    if (!this.reviewPayload?.agency) {
      return '';
    }
    const digit = this.reviewPayload.agencyDigit;
    const combined = digit
      ? `${this.reviewPayload.agency}-${digit}`
      : this.reviewPayload.agency;
    return formatRecipientAgency(combined);
  }

  get reviewAccountDisplay(): string {
    if (!this.reviewPayload?.account) {
      return '';
    }
    const digit = this.reviewPayload.accountDigit;
    const combined = digit
      ? `${this.reviewPayload.account}-${digit}`
      : this.reviewPayload.account;
    return formatRecipientAccount(combined);
  }

  get lookupPixKeyCustomMessages(): Record<string, string> {
    const regErrors =
      'features.recipientsBankAccounts.form.registration.errors';
    const formErrors = 'features.recipientsBankAccounts.form.errors';
    return {
      required: this.translate.instant(
        'features.recipientsBankAccounts.form.pixKeyRequired',
      ),
      pixKeyUnrecognized: this.translate.instant(
        `${regErrors}.pixKeyUnrecognized`,
      ),
      pixKeyIncomplete: this.translate.instant(`${regErrors}.pixKeyIncomplete`),
      invalidCPF: this.translate.instant(`${formErrors}.invalidCpf`),
      invalidCNPJ: this.translate.instant(`${formErrors}.invalidCnpj`),
      invalidPhone: this.translate.instant(`${regErrors}.invalidPhone`),
      invalidEmail: this.translate.instant(`${regErrors}.invalidEmail`),
      invalidUuid: this.translate.instant(`${formErrors}.invalidUuid`),
    };
  }

  get manualPixKeyCustomMessages(): Record<string, string> {
    return {
      required: this.translate.instant(
        'features.recipientsBankAccounts.form.pixKeyRequired',
      ),
      invalidPixKey: this.translate.instant(
        'features.recipientsBankAccounts.form.errors.invalidPixKey',
      ),
      invalidUuid: this.translate.instant(
        'features.recipientsBankAccounts.form.errors.invalidUuid',
      ),
    };
  }

  get manualHolderDocumentCustomMessages(): Record<string, string> {
    return {
      required: this.translate.instant(
        'features.recipientsBankAccounts.form.holderDocumentRequired',
      ),
      invalidDocument: this.translate.instant(
        'features.recipientsBankAccounts.form.errors.invalidDocument',
      ),
      invalidCPF: this.translate.instant(
        'features.recipientsBankAccounts.form.errors.invalidCpf',
      ),
      invalidCNPJ: this.translate.instant(
        'features.recipientsBankAccounts.form.errors.invalidCnpj',
      ),
    };
  }

  get manualHolderNameCustomMessages(): Record<string, string> {
    return {
      required: this.translate.instant(
        'features.recipientsBankAccounts.form.holderNameRequired',
      ),
    };
  }

  formatLookupPixKeyOnBlur(): void {
    const ctrl = this.lookupForm?.get('pixKey');
    if (!ctrl) return;
    const formatted = formatPixKeyForDisplay(
      String(ctrl.value ?? ''),
      this.lookupPixKeyKind,
      this.manualLookupKind,
    );
    if (formatted !== String(ctrl.value ?? '')) {
      ctrl.setValue(formatted, {emitEvent: false});
    }
  }

  pickAmbiguousLookupKind(kind: 'cpf' | 'phone'): void {
    this.manualLookupKind = kind;
    this.formatLookupPixKeyOnBlur();
    this.lookupForm.get('pixKey')?.updateValueAndValidity({emitEvent: false});
    this.lookupErrorMessage = null;
  }

  submitLookup(): void {
    if (this.lookupForm.invalid || this.lookupSubmitting) {
      this.lookupForm.markAllAsTouched();
      return;
    }

    if (this.needsAmbiguousKindChoice) {
      this.lookupErrorMessage = this.translate.instant(
        'features.recipientsBankAccounts.form.registration.lookup.kindAmbiguousPickFirst',
      );
      return;
    }

    const rawPixKey = String(this.lookupForm.get('pixKey')?.value ?? '').trim();
    const resolvedType = mapInferKindToBankAccountPixKeyType(
      this.effectiveLookupKind,
    );
    if (!resolvedType) {
      this.lookupForm.get('pixKey')?.setErrors({pixKeyUnrecognized: true});
      return;
    }

    const sanitizedPixKey = sanitizePixKeyForApi(rawPixKey, resolvedType);

    this.lookupErrorMessage = null;
    this.lookupNotFound = false;
    this.lookupSubmitting = true;
    this.actions
      .checkKeyFn(sanitizedPixKey, resolvedType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.lookupSubmitting = false;
          this.checkKeyResponse = response;
          this.reviewPayload = mapPixKeyCheckToBankAccountPayload(response, {
            pixKey: sanitizedPixKey,
            pixKeyType: resolvedType,
          });
          this.reviewSource = 'lookup';
          this.step = this.reviewStepIndex;
          this.maxAllowedIndex = this.reviewStepIndex;
        },
        error: (error: HttpErrorResponse) => {
          this.lookupSubmitting = false;
          this.handleLookupError(error, rawPixKey);
        },
      });
  }

  goToManualFromLookup(): void {
    const pixKey = String(this.lookupForm.get('pixKey')?.value ?? '').trim();
    this.prefillManualFromPixKey(pixKey);
    this.lookupNotFound = false;
    this.lookupErrorMessage = null;
    this.reviewSource = null;
    this.step = this.accountDataStepIndex;
    this.maxAllowedIndex = Math.max(
      this.maxAllowedIndex,
      this.accountDataStepIndex,
    );
  }

  continueFromAccountData(): void {
    if (this.manualForm.invalid) {
      this.manualForm.markAllAsTouched();
      return;
    }
    this.reviewPayload = this.buildPayloadFromManualForm();
    this.reviewSource = 'manual';
    this.step = this.reviewStepIndex;
    this.maxAllowedIndex = Math.max(this.maxAllowedIndex, this.reviewStepIndex);
  }

  backFromReview(): void {
    if (this.reviewSource === 'lookup') {
      this.checkKeyResponse = null;
      this.reviewPayload = null;
      this.reviewSource = null;
      this.lookupErrorMessage = null;
      this.lookupNotFound = false;
      this.step = 0;
      this.maxAllowedIndex = 0;
      return;
    }
    this.step = this.accountDataStepIndex;
  }

  backToPixKeyStep(): void {
    this.lookupErrorMessage = null;
    this.lookupNotFound = false;
    this.step = 0;
  }

  confirmReview(): void {
    if (!this.reviewPayload || this.reviewSubmitting) {
      return;
    }
    this.reviewSubmitting = true;
    this.actions
      .createFn(this.reviewPayload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: account => {
          this.reviewSubmitting = false;
          setTimeout(
            () => this.created.emit(account),
            POST_SAVE_REDIRECT_DELAY_MS,
          );
        },
        error: (error: HttpErrorResponse) => {
          this.reviewSubmitting = false;
          this.showSaveError(error);
        },
      });
  }

  formatManualHolderDocumentOnBlur(): void {
    const ctrl = this.manualForm?.get('holderDocument');
    if (!ctrl) return;
    const formatted = formatCpfCnpjDisplay(ctrl.value);
    if (formatted !== String(ctrl.value ?? '')) {
      ctrl.setValue(formatted, {emitEvent: false});
    }
  }

  cancel(): void {
    this.cancelled.emit();
  }

  getAccountTypeLabel(type: BankAccountType): string {
    return (
      this.accountTypeOptions.find(option => option.value === type)?.label ??
      String(type)
    );
  }

  private handleLookupError(error: HttpErrorResponse, pixKey: string): void {
    const base = 'features.recipientsBankAccounts.form.registration.errors';

    if (error.status === 404) {
      this.lookupNotFound = true;
      this.lookupErrorMessage = this.translate.instant(`${base}.notFound`);
      return;
    }

    if (error.status === 501) {
      this.capabilityUnavailable = true;
      this.alerts
        .open(this.translate.instant(`${base}.unavailable`), {
          label: this.translate.instant('common.status.error'),
          appearance: 'warning',
        })
        .subscribe();
      this.prefillManualFromPixKey(pixKey);
      this.lookupNotFound = false;
      this.lookupErrorMessage = null;
      this.reviewSource = null;
      this.step = 0;
      this.maxAllowedIndex = 0;
      this.refreshLocalizedUi();
      return;
    }

    if (error.status === 400) {
      this.alerts
        .open(
          error.error?.message ??
            this.translate.instant(`${base}.invalidRequest`),
          {
            label: this.translate.instant('common.status.error'),
            appearance: 'error',
          },
        )
        .subscribe();
      return;
    }

    this.lookupNotFound = false;
    this.lookupErrorMessage = this.translate.instant(`${base}.generic`);
  }

  private prefillManualFromPixKey(pixKey: string): void {
    const inferredType = mapInferKindToBankAccountPixKeyType(
      this.manualLookupKind === 'cpf'
        ? 'cpf'
        : this.manualLookupKind === 'phone'
        ? 'phone'
        : inferPixKeyKind(pixKey),
    );
    this.manualForm.patchValue({
      pixKey,
      ...(inferredType ? {pixKeyType: inferredType} : {}),
    });
  }

  private buildPayloadFromManualForm(): CreateRecipientBankAccountPayload {
    const value = this.manualForm.getRawValue();
    const pixKeyType = value.pixKeyType as PixKeyType;
    return {
      pixKeyType,
      pixKey: sanitizePixKeyForApi(String(value.pixKey ?? ''), pixKeyType),
      holderName: value.holderName,
      holderDocument: String(value.holderDocument ?? '').replace(/\D/g, ''),
      compeCode: value.compeCode || undefined,
      agency: value.agency || undefined,
      agencyDigit: value.agencyDigit || undefined,
      account: value.account || undefined,
      accountDigit: value.accountDigit || undefined,
      accountType: value.accountType || undefined,
    };
  }

  private showSaveError(error: HttpErrorResponse): void {
    this.alerts
      .open(
        error.error?.message ??
          this.translate.instant('common.errorSavingData'),
        {
          label: this.translate.instant('common.status.error'),
          appearance: 'error',
        },
      )
      .subscribe();
  }

  private refreshLocalizedUi(): void {
    const base = 'features.recipientsBankAccounts.form.registration.stepper';
    if (this.showPixLookupStep) {
      this.wizardSteps = [
        {label: this.translate.instant(`${base}.pixKey`)},
        {label: this.translate.instant(`${base}.accountData`)},
        {label: this.translate.instant(`${base}.review`)},
      ];
    } else {
      this.wizardSteps = [
        {label: this.translate.instant(`${base}.accountData`)},
        {label: this.translate.instant(`${base}.review`)},
      ];
    }
    this.buildPixKeyTypeOptions();
    this.buildAccountTypeOptions();
  }

  private buildLookupForm(): void {
    this.lookupForm = this.fb.group({
      pixKey: ['', [Validators.required, this.lookupPixKeyValidator]],
    });
    this.lookupForm
      .get('pixKey')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.lookupPixKeyKind !== 'cpf_or_phone') {
          this.manualLookupKind = null;
        }
        if (this.lookupNotFound) {
          this.lookupNotFound = false;
          this.lookupErrorMessage = null;
        }
      });
  }

  private lookupPixKeyValidator = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const raw = String(control.value ?? '').trim();
    if (!raw) {
      return null;
    }
    const inferred = inferPixKeyKind(raw);
    const kind =
      this.manualLookupKind === 'cpf'
        ? 'cpf'
        : this.manualLookupKind === 'phone'
        ? 'phone'
        : inferred;
    return validatePixKeyByKind(raw, kind);
  };

  private buildManualForm(): void {
    this.manualForm = this.fb.group({
      pixKeyType: [PixKeyType.EMAIL, [Validators.required]],
      pixKey: [
        '',
        [Validators.required, recipientBankAccountPixKeyCrossValidator],
      ],
      holderName: ['', [Validators.required]],
      holderDocument: ['', [Validators.required, validateCPFOrCNPJ]],
      compeCode: [''],
      agency: [''],
      agencyDigit: [''],
      account: [''],
      accountDigit: [''],
      accountType: [null as BankAccountType | null],
    });

    this.syncManualPixKeyMask();
    this.manualForm
      .get('pixKey')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncManualPixKeyMask());
    this.manualForm
      .get('pixKeyType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.syncManualPixKeyMask();
        this.manualForm
          .get('pixKey')
          ?.updateValueAndValidity({emitEvent: false});
      });
  }

  private syncManualPixKeyMask(): void {
    const type = this.manualForm?.get('pixKeyType')?.value as
      | PixKeyType
      | undefined;
    const pix = this.manualForm?.get('pixKey')?.value;
    this.manualPixKeyMask = pixKeyDocumentMask(
      type === undefined || type === null ? '' : String(type),
      pix === null || pix === undefined ? '' : String(pix),
    );
    this.manualPixKeyMaxLength =
      type === PixKeyType.DOCUMENT ? cpfCnpjFormattedMaxLength : 320;
  }

  private buildPixKeyTypeOptions(): void {
    const values = Object.values(PixKeyType);
    const keys = values.map(v => `enums.pixKeyTypes.${v}`);
    this.translate.get(keys).subscribe(translations => {
      this.pixKeyTypeOptions = values.map(value => ({
        value,
        label: translations[`enums.pixKeyTypes.${value}`] ?? value,
      }));
    });
  }

  private buildAccountTypeOptions(): void {
    this.translate
      .get([
        'features.recipientsBankAccounts.form.registration.manual.typeChecking',
        'features.recipientsBankAccounts.form.registration.manual.typeSavings',
      ])
      .subscribe(translations => {
        this.accountTypeOptions = [
          {
            value: BankAccountType.CHECKING,
            label:
              translations[
                'features.recipientsBankAccounts.form.registration.manual.typeChecking'
              ],
          },
          {
            value: BankAccountType.SAVINGS,
            label:
              translations[
                'features.recipientsBankAccounts.form.registration.manual.typeSavings'
              ],
          },
        ];
      });
  }
}
